import React, {ReactNode, useEffect, useState} from 'react';
import {classes, st} from './Layout.st.css';
import {MainLoader} from './MainLoader/MainLoader';
import {AsideLoader} from './AsideLoader/AsideLoader';
import {HeaderLoader} from './HeaderLoader/HeaderLoader';
import {useEnvironment, useExperiments} from '@wix/yoshi-flow-editor';
import {A11yIds, SPECS} from '../../constants';
import {useLocaleKeys} from '../../../locale-keys/LocaleKeys';
import {Spinner} from '@wix/wix-ui-tpa/cssVars';

export interface LayoutProps {
  isLoading: boolean;
  showErrorState?: boolean;
  isOneColumnView?: boolean;
  Header: () => ReactNode;
  Aside: () => ReactNode;
  Footer: () => ReactNode;
  ErrorState: () => ReactNode;
  children: ReactNode;
  dataHook: string;
  dir: string;
}

export enum LayoutDataHooks {
  mainSection = 'LayoutDataHooks.mainSection',
  oneColumnViewSummary = 'LayoutDataHooks.oneColumnViewSummary',
  twoColumnViewSummary = 'LayoutDataHooks.twoColumnViewSummary',
  verticalDivider = 'LayoutDataHooks.verticalDivider',
  spinner = 'LayoutDataHooks.spinner',
}

/* eslint-disable sonarjs/cognitive-complexity */
export const Layout = ({
  dataHook,
  dir,
  isLoading,
  showErrorState,
  isOneColumnView,
  Header,
  Aside,
  Footer,
  ErrorState,
  children,
}: LayoutProps) => {
  const {experiments} = useExperiments();
  const layoutCSSFix = experiments.enabled(SPECS.LayoutCSSFixOnCheckout);
  const localeKeys = useLocaleKeys();
  const [loaded, setLoaded] = useState(false);

  const {isEditor} = useEnvironment();

  useEffect(() => {
    setLoaded(!isLoading);
  }, [isLoading]);

  if (experiments.enabled(SPECS.ShowCheckoutOnEditorPreview) && isEditor) {
    return (
      <div className={classes.spinnerContainer}>
        <Spinner data-hook={LayoutDataHooks.spinner} isCentered={true} />
      </div>
    );
  }

  return (
    <div
      data-hook={dataHook}
      className={st(
        layoutCSSFix ? classes.root : /* istanbul ignore next */ classes.rootOld,
        showErrorState ? classes.errorStateContainer : undefined
      )}
      dir={dir}>
      {loaded || showErrorState ? Header() : <HeaderLoader />}
      {showErrorState ? (
        ErrorState()
      ) : (
        <div className={layoutCSSFix ? classes.container : /* istanbul ignore next */ classes.containerOld}>
          {isOneColumnView && (
            <aside data-hook={LayoutDataHooks.oneColumnViewSummary} className={classes.aside}>
              {loaded ? Aside() : null}
            </aside>
          )}
          <section
            className={classes.main}
            data-hook={LayoutDataHooks.mainSection}
            aria-label={localeKeys.checkout.checkoutForm.ariaLabel()}>
            {loaded ? children : <MainLoader />}
          </section>
          {!isOneColumnView && <div data-hook={LayoutDataHooks.verticalDivider} className={classes.divider} />}
          {(!isOneColumnView || !loaded) && (
            <section
              aria-labelledby={A11yIds.SummarySection}
              data-hook={LayoutDataHooks.twoColumnViewSummary}
              className={classes.aside}>
              {loaded ? Aside() : <AsideLoader />}
            </section>
          )}
        </div>
      )}
      {loaded && Footer()}
    </div>
  );
};
